import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Maximize2, MessageCircle, Minimize2, Minus, X } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { isAks2talOnly } from '../../components/AdminOnly';
import { useAppSelector } from '../../hooks';
import { callbackPath, consumeReturnTo } from './navigation';
import './floating-chat.css';

const PersonalAssistant = lazy(() => import('./index'));

export default function FloatingPipedriveChat() {
  const uid = useAppSelector((state) => state.user.details.uid);
  return uid && isAks2talOnly(uid) ? <FloatingChat key={uid} uid={uid} /> : null;
}

// Kept solely for registered OAuth callbacks and existing bookmarks.
export function PipedriveAssistantCallback() {
  const uid = useAppSelector((state) => state.user.details.uid);
  return uid && isAks2talOnly(uid) ? (
    <div role="status" style={{ padding: 24 }}>
      Åbner AKS-assistenten…
    </div>
  ) : (
    <Redirect to={uid ? '/groups' : '/sign-in'} />
  );
}

function FloatingChat({ uid }: { uid: string }) {
  const location = useLocation();
  const history = useHistory();
  const [open, setOpen] = useState(location.pathname === callbackPath);
  const [hasOpened, setHasOpened] = useState(open);
  const [expanded, setExpanded] = useState(false);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const minimizeRef = useRef<HTMLButtonElement>(null);
  const [viewport, setViewport] = useState<{ height: number; top: number }>();

  useEffect(() => {
    if (location.pathname === callbackPath) {
      setHasOpened(true);
      setOpen(true);
    }
  }, [location.pathname]);

  // Keep the composer above the software keyboard, including iOS Safari.
  useEffect(() => {
    if (!window.visualViewport) return;
    const visualViewport = window.visualViewport;
    const update = () =>
      setViewport({ height: visualViewport.height, top: visualViewport.offsetTop });
    update();
    visualViewport.addEventListener('resize', update);
    visualViewport.addEventListener('scroll', update);
    return () => {
      visualViewport.removeEventListener('resize', update);
      visualViewport.removeEventListener('scroll', update);
    };
  }, []);

  useEffect(() => {
    if (open) minimizeRef.current?.focus({ preventScroll: true });
  }, [open]);

  const minimize = () => {
    setOpen(false);
    launcherRef.current?.focus({ preventScroll: true });
  };

  const onCallbackHandled = useCallback(() => {
    history.replace(consumeReturnTo(uid));
  }, [history, uid]);

  return createPortal(
    <div
      className={`pipedrive-chat-widget no-print${
        process.env.NODE_ENV === 'development' ? ' pipedrive-chat-widget-dev' : ''
      }`}
      style={viewport ? { height: viewport.height, top: viewport.top } : undefined}
    >
      {hasOpened && (
        <section
          id="pipedrive-chat-panel"
          role="dialog"
          aria-modal="false"
          aria-labelledby="pipedrive-chat-title"
          className={`pipedrive-chat-dialog${expanded ? ' pipedrive-chat-dialog-expanded' : ''}`}
          hidden={!open}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.stopPropagation();
              minimize();
            }
          }}
        >
          <header className="pipedrive-chat-header">
            <span className="pipedrive-chat-avatar" aria-hidden="true">
              <MessageCircle className="size-4" />
            </span>
            <div className="pipedrive-chat-heading">
              <strong id="pipedrive-chat-title">AKS-assistent</strong>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="size-8 text-muted-foreground"
              aria-label={expanded ? 'Gør chat mindre' : 'Udvid chat'}
              title={expanded ? 'Gør chat mindre' : 'Udvid chat'}
              onClick={() => setExpanded((value) => !value)}
            >
              {expanded ? <Minimize2 /> : <Maximize2 />}
            </Button>
            <Button
              ref={minimizeRef}
              variant="ghost"
              size="icon"
              className="size-8 text-muted-foreground"
              aria-label="Minimer AKS-chat"
              title="Minimer chat"
              onClick={minimize}
            >
              <Minus />
            </Button>
          </header>
          <div className="pipedrive-chat-panel">
            <Suspense
              fallback={
                <p
                  role="status"
                  className="flex items-center gap-2 p-6 text-sm text-muted-foreground"
                >
                  <Loader2 className="size-4 animate-spin" /> Åbner chat…
                </p>
              }
            >
              <PersonalAssistant onCallbackHandled={onCallbackHandled} />
            </Suspense>
          </div>
        </section>
      )}
      <Button
        ref={launcherRef}
        className="pipedrive-chat-launcher [&_svg]:size-6"
        size="icon"
        aria-label={open ? 'Luk AKS-chat' : 'Åbn AKS-chat'}
        title={open ? 'Luk AKS-chat' : 'AKS-chat'}
        aria-expanded={open}
        aria-controls={hasOpened ? 'pipedrive-chat-panel' : undefined}
        aria-haspopup="dialog"
        onClick={() => {
          if (open) minimize();
          else {
            setHasOpened(true);
            setOpen(true);
          }
        }}
      >
        {open ? <X /> : <MessageCircle />}
      </Button>
    </div>,
    document.body
  );
}
